import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	Box,
	Divider,
	Grid,
	GlobalStyles,
	Stack,
	Typography,
} from '@mui/material';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import type { Column } from 'react-data-grid';

import MultiLineChartWidget from 'components/Widgets/MultiLineChartWidget';
import { getDynamicLineColor } from 'components/Widgets/chartWidgetUtility';
import ThreeDPieChart from 'components/Widgets/ThreeDPieChart';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import SearchableDropdown, {
	type SearchableDropdownOption,
} from 'components/Dropdown/SearchableDropdown';
import PageWrapper from 'components/Common/PageWraper';
import RowTypography from 'components/Table/RowTypography';
import {
	getCloudSpendFilters,
	getProgramCostLine,
	getProgramCostTrendTable,
	getServiceCostTable,
	getConsumptionMetricTable,
	getCostAvoidanceTable,
} from 'service/service';

interface PieChartData {
	name: string;
	value: number;
	color?: string;
	percentage?: string;
}

type DynamicLineChartRow = {
	month: string;
	[key: string]: string | number;
};

interface LineSeries {
	key: string;
	name: string;
	color?: string;
	smooth?: boolean;
	symbolSize?: number;
}

interface MonthlySpendTableRow {
	id: number;
	service: string;
	grandTotal: number;
	[key: string]: string | number;
}

interface MonthDefinition {
	key: string;
	label: string;
	sortValue: number;
}

interface ProgramCostLineApiRow {
	program: string;
	month: string;
	cost: number;
}

interface ServiceCostTableApiRow {
	service: string;
	grandTotal: number;
	[key: string]: string | number;
}

interface ProgramCostTrendTableApiRow {
	program: string;
	grandTotal: number;
	[key: string]: string | number;
}

interface FilterOptionsApiResponse {
	programs?: string[];
	envs?: string[];
	divisions?: string[];
	markets?: string[];
}

interface ConsumptionMetricTableRow {
	id: number;
	CONSUMPTION_METRIC: string;
	[key: string]: string | number;
}

interface CostAvoidanceTableRow {
	id: number;
	Sector: string;
	[key: string]: string | number;
}

interface CloudSpendFilters {
	program?: string;
	env?: string;
	division?: string;
	market?: string;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
}

interface SortingInfo {
	sortBy: string;
	sortOrder: 'asc' | 'desc' | '';
}

const SERVICE_COLORS = [
	'#8172F3',
	'#FF8D87',
	'#27B8D2',
	'#FFA63A',
	'#006C8C',
];

const PIE_COLORS = [
	'#1239F4',
	'#FF8B84',
	'#16C4E6',
	'#3798FF',
];

// A fixed color reserved for the "All Programs" aggregate line so it never
// collides with getDynamicLineColor's hue rotation, however many programs exist.
const ALL_PROGRAMS_COLOR = '#0F2C5C';

const ALL_PROGRAM_OPTION: SearchableDropdownOption = {
	label: 'All Programs',
	value: 'all',
};

const ALL_ENVIRONMENT_OPTION: SearchableDropdownOption = {
	label: 'All Environments',
	value: 'all',
};

const ALL_MARKET_OPTION: SearchableDropdownOption = {
	label: 'All Markets',
	value: 'all',
};


const ALL_SUBPROGRAM_OPTION: SearchableDropdownOption = {
	label: 'All Sub Programs',
	value: 'all',
};


const DEFAULT_MONTHS: MonthDefinition[] = [
	{
		key: 'month_2026_01',
		label: 'JAN 2026',
		sortValue: 202601,
	},
	{
		key: 'month_2026_02',
		label: 'FEB 2026',
		sortValue: 202602,
	},
	{
		key: 'month_2026_03',
		label: 'MAR 2026',
		sortValue: 202603,
	},
	{
		key: 'month_2026_04',
		label: 'APR 2026',
		sortValue: 202604,
	},
	{
		key: 'month_2026_05',
		label: 'MAY 2026',
		sortValue: 202605,
	},
	{
		key: 'month_2026_06',
		label: 'JUN 2026',
		sortValue: 202606,
	},
	{
		key: 'month_2026_07',
		label: 'JUL 2026',
		sortValue: 202607,
	},
	{
		key: 'month_2026_08',
		label: 'AUG 2026',
		sortValue: 202608,
	},
	{
		key: 'month_2026_09',
		label: 'SEP 2026',
		sortValue: 202609,
	},
	{
		key: 'month_2026_10',
		label: 'OCT 2026',
		sortValue: 202610,
	},
	{
		key: 'month_2026_11',
		label: 'NOV 2026',
		sortValue: 202611,
	},
	{
		key: 'month_2026_12',
		label: 'DEC 2026',
		sortValue: 202612,
	}
];

const getMonthDefinition = (
	month: string,
): MonthDefinition | null => {
	const monthValue = String(month ?? '').trim();

	if (!monthValue) {
		return null;
	}

	const parts = monthValue.split(/[-/\s]+/).filter(Boolean);
	let monthNumber: number | null = null;
	let year: number | null = null;

	if (parts.length >= 2) {
		const firstPart = Number(parts[0]);
		const secondPart = Number(parts[1]);

		if (firstPart >= 1000) {
			year = firstPart;
			monthNumber = secondPart;
		} else {
			monthNumber = firstPart;
			year = secondPart;
		}
	}

	if (
		monthNumber !== null &&
		year !== null &&
		monthNumber >= 1 &&
		monthNumber <= 12 &&
		year >= 1000
	) {
		const date = new Date(year, monthNumber - 1, 1);

		return {
			key: `month_${year}_${String(monthNumber).padStart(2, '0')}`,
			label: new Intl.DateTimeFormat('en-US', {
				month: 'short',
				year: 'numeric',
			})
				.format(date)
				.toUpperCase(),
			sortValue: year * 100 + monthNumber,
		};
	}

	// Only attempt Date parsing for strings starting with an actual month
	// name (e.g. "Jan-26", "January 2026") — Date's constructor is
	// dangerously lenient and will happily "parse" unrelated labels like
	// "TOTAL-2026" or "AOP-2027" into a bogus January date, silently
	// colliding with the real January column.
	const looksLikeDate =
		/^(jan(uary)?|feb(ruary)?|mar(ch)?|apr(il)?|may|jun(e)?|jul(y)?|aug(ust)?|sep(t|tember)?|oct(ober)?|nov(ember)?|dec(ember)?)[-/\s]+\d{2,4}$/i.test(
			monthValue,
		);
	const parsedDate = looksLikeDate ? new Date(monthValue) : new Date(NaN);

	if (!Number.isNaN(parsedDate.getTime())) {
		const parsedYear = parsedDate.getFullYear();
		const parsedMonth = parsedDate.getMonth() + 1;

		return {
			key: `month_${parsedYear}_${String(parsedMonth).padStart(2, '0')}`,
			label: new Intl.DateTimeFormat('en-US', {
				month: 'short',
				year: 'numeric',
			})
				.format(parsedDate)
				.toUpperCase(),
			sortValue: parsedYear * 100 + parsedMonth,
		};
	}

	const upperVal = monthValue.toUpperCase();
	const specialSortOrder: Record<string, number> = {
		'Q1-2026': 202613,
		'Q2-2026': 202614,
		'H1-2026': 202615,
		'Q3-2026': 202616,
		'Q4-2026': 202617,
		'H2-2026': 202618,
		'ACTUAL-2025': 202619,
		'TOTAL-2026': 202620,
		'BL-2027': 202701,
		'NEWINCREMENT-2027': 202702,
		'AOP-2027': 202703,
	};

	if (specialSortOrder[upperVal] !== undefined) {
		return {
			key: `month_${monthValue
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, '_')}`,
			label: upperVal,
			sortValue: specialSortOrder[upperVal],
		};
	}

	return {
		key: `month_${monthValue
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '_')}`,
		label: upperVal,
		sortValue: Number.MAX_SAFE_INTEGER,
	};
};

const getMonthDefinitions = (
	rows: ProgramCostLineApiRow[],
): MonthDefinition[] => {
	const months = new Map<string, MonthDefinition>();

	DEFAULT_MONTHS.forEach((month) => {
		months.set(month.key, month);
	});

	rows.forEach((row) => {
		const month = getMonthDefinition(row.month);

		if (month) {
			months.set(month.key, month);
		}
	});

	return Array.from(months.values()).sort(
		(firstMonth, secondMonth) =>
			firstMonth.sortValue - secondMonth.sortValue,
	);
};

// Appends a "Grand Total" row summing every month column plus the
// grandTotal column across all data rows, matching the source Excel's
const getVisibleMonthsForTable = (
	tableType: string,
	monthDefinitions: MonthDefinition[],
): MonthDefinition[] => {
	try {
		const stored = localStorage.getItem(`${tableType}_user_col_pref`);
		if (stored) {
			const visibleColNames: string[] = JSON.parse(stored);
			if (Array.isArray(visibleColNames) && visibleColNames.length > 0) {
				const filtered = monthDefinitions.filter((m) =>
					visibleColNames.includes(m.label),
				);
				if (filtered.length > 0) {
					return filtered;
				}
			}
		}
	} catch {
		/* ignore */
	}
	return monthDefinitions;
};

// Appends a "Grand Total" row summing every month column plus the
// grandTotal column across all data rows for selected/visible months.
const appendGrandTotalRow = (
	rows: MonthlySpendTableRow[],
	monthDefinitions: MonthDefinition[],
	visibleMonths: MonthDefinition[],
): MonthlySpendTableRow[] => {
	if (!rows.length) return rows;

	const totalsRow: MonthlySpendTableRow = {
		id: rows.length + 1,
		service: 'Grand Total',
		grandTotal: 0,
	};

	monthDefinitions.forEach((month) => {
		totalsRow[month.key] = 0;
	});

	rows.forEach((row) => {
		monthDefinitions.forEach((month) => {
			totalsRow[month.key] =
				Number(totalsRow[month.key] ?? 0) +
				Number(row[month.key] ?? 0);
		});
	});

	// Sum raw unrounded month totals for visible months first, then format at display time
	totalsRow.grandTotal = visibleMonths.reduce(
		(sum, month) => sum + Number(totalsRow[month.key] ?? 0),
		0,
	);

	return [...rows, totalsRow];
};

const createServiceCostTableData = (
	rows: ServiceCostTableApiRow[],
	tableType?: string,
): {
	data: MonthlySpendTableRow[];
	months: MonthDefinition[];
} => {
	const months = new Map<string, MonthDefinition>();

	DEFAULT_MONTHS.forEach((month) => {
		months.set(month.key, month);
	});

	rows.forEach((row) => {
		Object.keys(row).forEach((key) => {
			if (key === 'service' || key === 'grandTotal') {
				return;
			}

			const month = getMonthDefinition(key);

			if (month) {
				months.set(month.key, month);
			}
		});
	});

	const monthDefinitions = Array.from(months.values()).sort(
		(firstMonth, secondMonth) =>
			firstMonth.sortValue - secondMonth.sortValue,
	);

	const visibleMonths = tableType
		? getVisibleMonthsForTable(tableType, monthDefinitions)
		: monthDefinitions;

	const isAllMonthsSelected = visibleMonths.length === monthDefinitions.length;

	const data = rows.map((row, index) => {
		const tableRow: MonthlySpendTableRow = {
			id: index + 1,
			service: String(row.service ?? '').trim(),
			grandTotal: 0,
		};

		monthDefinitions.forEach((month) => {
			tableRow[month.key] = 0;
		});

		Object.entries(row).forEach(([key, value]) => {
			if (key === 'service' || key === 'grandTotal') {
				return;
			}

			const month = getMonthDefinition(key);

			if (month) {
				tableRow[month.key] = Number(value ?? 0);
			}
		});

		if (isAllMonthsSelected && row.grandTotal != null) {
			tableRow.grandTotal = Number(row.grandTotal ?? 0);
		} else {
			tableRow.grandTotal = visibleMonths.reduce(
				(sum, month) => sum + Number(tableRow[month.key] ?? 0),
				0,
			);
		}

		return tableRow;
	});

	return {
		data: appendGrandTotalRow(data, monthDefinitions, visibleMonths),
		months: monthDefinitions,
	};
};

const createProgramCostTrendTableData = (
	rows: ProgramCostTrendTableApiRow[],
	tableType?: string,
): {
	data: MonthlySpendTableRow[];
	months: MonthDefinition[];
} => {
	const months = new Map<string, MonthDefinition>();

	DEFAULT_MONTHS.forEach((month) => {
		months.set(month.key, month);
	});

	rows.forEach((row) => {
		Object.keys(row).forEach((key) => {
			if (key === 'program' || key === 'grandTotal') {
				return;
			}

			const month = getMonthDefinition(key);

			if (month) {
				months.set(month.key, month);
			}
		});
	});

	const monthDefinitions = Array.from(
		months.values(),
	).sort(
		(firstMonth, secondMonth) =>
			firstMonth.sortValue - secondMonth.sortValue,
	);

	const visibleMonths = tableType
		? getVisibleMonthsForTable(tableType, monthDefinitions)
		: monthDefinitions;

	const isAllMonthsSelected = visibleMonths.length === monthDefinitions.length;

	const data = rows.map((row, index) => {
		const tableRow: MonthlySpendTableRow = {
			id: index + 1,
			service: String(row.program ?? '').trim(),
			grandTotal: 0,
		};

		monthDefinitions.forEach((month) => {
			tableRow[month.key] = 0;
		});

		Object.entries(row).forEach(([key, value]) => {
			if (key === 'program' || key === 'grandTotal') {
				return;
			}

			const month = getMonthDefinition(key);

			if (month) {
				tableRow[month.key] = Number(value ?? 0);
			}
		});

		if (isAllMonthsSelected && row.grandTotal != null) {
			tableRow.grandTotal = Number(row.grandTotal ?? 0);
		} else {
			tableRow.grandTotal = visibleMonths.reduce(
				(sum, month) => sum + Number(tableRow[month.key] ?? 0),
				0,
			);
		}

		return tableRow;
	});

	return {
		data: appendGrandTotalRow(data, monthDefinitions, visibleMonths),
		months: monthDefinitions,
	};
};

const createSeriesKey = (
	service: string,
	index: number,
) => {
	const normalized = String(service ?? '')
		.trim()
		.toLowerCase()
		.split(/[^a-z0-9]+/)
		.filter(Boolean)
		.map((part, index) =>
			index === 0
				? part
				: `${part.charAt(0).toUpperCase()}${part.slice(1)}`,
		)
		.join('');

	return normalized || `service${index}`;
};

export const createLineChartData = (
	rows: ProgramCostLineApiRow[],
): {
	data: DynamicLineChartRow[];
	series: LineSeries[];
} => {
	const programs = Array.from(
		new Set(
			rows
				.map((row) => String(row.program ?? '').trim())
				.filter(Boolean),
		),
	);

	// 'allPrograms' is reserved for the synthetic aggregate series below — if a
	// real program's name would camelCase to the same key, force it to collide
	// with the dedup counter instead of the reserved key.
	const usedKeys = new Set<string>(['allPrograms']);
	const programDefinitions = programs.map((program, index) => {
		const baseKey = createSeriesKey(program, index);
		let key = baseKey;
		let duplicateIndex = 1;

		while (usedKeys.has(key)) {
			key = `${baseKey}${duplicateIndex}`;
			duplicateIndex += 1;
		}

		usedKeys.add(key);

		return {
			program,
			key,
		};
	});

	const programKeyMap = new Map(
		programDefinitions.map((item) => [item.program, item.key]),
	);
	const months = getMonthDefinitions(rows);
	const monthRows = new Map<string, DynamicLineChartRow>();

	rows.forEach((row) => {
		const month = getMonthDefinition(row.month);
		const programKey = programKeyMap.get(
			String(row.program ?? '').trim(),
		);

		if (!month || !programKey) {
			return;
		}

		const currentRow = monthRows.get(month.key) ?? {
			month: month.label,
			allPrograms: 0,
			...Object.fromEntries(
				programDefinitions.map(({ key }) => [key, 0]),
			),
		};

		const cost = Number(row.cost ?? 0);
		currentRow[programKey] = Number(currentRow[programKey] ?? 0) + cost;
		// Sums every program's cost for this month into the "All Programs"
		// aggregate — computed unconditionally here so visibleLineChartSeries
		// only ever toggles which series is shown, never recomputes sums.
		currentRow.allPrograms = Number(currentRow.allPrograms ?? 0) + cost;
		monthRows.set(month.key, currentRow);
	});

	return {
		data: months.map((month) => ({
			month: month.label,
			allPrograms: 0,
			...Object.fromEntries(
				programDefinitions.map(({ key }) => [key, 0]),
			),
			...(monthRows.get(month.key)),
		})),
		// Color is assigned here from each program's position in the full,
		// unfiltered program list so it stays stable when the PROGRAM
		// dropdown later filters lineChartSeries down to one entry — the
		// chart utility's own index-based color assignment would otherwise
		// recompute against the filtered array's length and always land on
		// the same color regardless of which program was selected.
		series: [
			{
				key: 'allPrograms',
				name: 'All Programs',
				color: ALL_PROGRAMS_COLOR,
				smooth: false,
				symbolSize: 5,
			},
			...programDefinitions.map(({ program, key }, index) => ({
				key,
				name: program,
				color: getDynamicLineColor(
					index,
					programDefinitions.length,
				),
				smooth: false,
				symbolSize: 5,
			})),
		],
	};
};

export const selectVisibleLineChartSeries = (
	series: LineSeries[],
	selectedProgram: SearchableDropdownOption | null,
): LineSeries[] => {
	if (!selectedProgram?.value || selectedProgram.value === 'all') {
		return series.filter((item) => item.key === 'allPrograms');
	}

	return series.filter((item) => item.name === selectedProgram.value);
};

// Reuses the already-loaded service cost table (sorted by Grand Total,
// top 15) to build a top-5-services pie without a dedicated endpoint.
// Excludes the "Grand Total" summary row appended by appendGrandTotalRow.
const createServiceTopFivePieData = (
	rows: MonthlySpendTableRow[],
): PieChartData[] =>
	rows
		.filter((row) => row.service !== 'Grand Total')
		.slice()
		.sort((a, b) => b.grandTotal - a.grandTotal)
		.slice(0, 5)
		.map((row) => ({
			name: row.service,
			value: Number(row.grandTotal),
		}));

const buildRequestBody = (
	program: SearchableDropdownOption | null,
	environment: SearchableDropdownOption | null,
	division: SearchableDropdownOption | null,
	market: SearchableDropdownOption | null,
	sortingInfo?: SortingInfo,
): CloudSpendFilters => {
	const filters: CloudSpendFilters = {};

	if (program?.value && program.value !== 'all') {
		filters.program = String(program.value);
	}

	if (
		environment?.value &&
		environment.value !== 'all'
	) {
		filters.env = String(environment.value);
	}

	if (market?.value && market.value !== 'all') {
		filters.market = String(market.value);
	}

	if (division?.value && division.value !== 'all') {
		filters.division = String(division.value);
	}

	if (sortingInfo?.sortBy && sortingInfo.sortOrder) {
		filters.sortBy = sortingInfo.sortBy;
		filters.sortOrder = sortingInfo.sortOrder;
	}

	return filters;
};

const formatMillionsValue = (value: number) =>
	Number(value ?? 0).toLocaleString('en-US', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 3,
	});

const formatValue = (value: number) =>
	Number(value ?? 0).toLocaleString('en-US');

const renderTableValue = (
	value: string | number,
	fontWeight = 500,
) => (
	<RowTypography
		sx={{
			fontWeight,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		}}
	>
		{value}
	</RowTypography>
);

// Jul-Dec 2026 are forecasted months (matches the line chart's
// forecastStartMonth), visually distinguished from the actuals in Jan-Jun
// with a tinted background. Scoped to 2026 specifically (not just "month
// >= 7" for any year) so non-month columns (AOP, Q1-Q4, TOTAL, etc., whose
// sortValue is Number.MAX_SAFE_INTEGER) and future-year months (e.g. a
// real Jan 2027) never get this coloring.
const isForecastMonth = (month: MonthDefinition) =>
	month.sortValue >= 202607 && month.sortValue <= 202612;

// Per TL feedback: not a new/foreign color — just a deeper shade of the
// table's own existing zebra colors (white row -> pale blue, light-blue row
// -> a richer blue), blended toward the brand blue (#003771) instead of
// toward black so it reads as "more blue", not gray. Hardcoded (not derived
// from the live theme) since this table only ships in the light theme today.
const FORECAST_ODD_CELL_BG = '#E0E7EE';
const FORECAST_EVEN_CELL_BG = '#C9D7EE';
const FORECAST_HEADER_BG = '#00264F';

const getColumns = (
	months: MonthDefinition[],
	valueFormatter: (value: number) => string,
	firstColumnName = 'SERVICE',
): Array<Column<MonthlySpendTableRow>> => {
	const monthColumns = months.map<Column<MonthlySpendTableRow>>(
		(month) => ({
			key: month.key,
			name: month.label,
			resizable: true,
			minWidth: 105,
			headerCellClass: isForecastMonth(month)
				? 'forecast-month-header'
				: undefined,
			cellClass: isForecastMonth(month)
				? 'forecast-month-cell'
				: undefined,
			formatter: ({ row }) =>
				renderTableValue(
					valueFormatter(
						Number(row[month.key] ?? 0),
					),
					row.service === 'Grand Total' ? 700 : 500,
				),
		}),
	);

	return [
		{
			key: 'service',
			name: firstColumnName,
			resizable: true,
			minWidth: 165,
			frozen: true,
			formatter: ({ row }) =>
				renderTableValue(row.service, row.service === 'Grand Total' ? 700 : 500),
		},
		...monthColumns,
		{
			key: 'grandTotal',
			name: 'GRAND TOTAL',
			resizable: true,
			minWidth: 125,
			formatter: ({ row }) =>
				renderTableValue(
					valueFormatter(row.grandTotal),
					700,
				),
		},
	];
};

const CloudSpendFinOpsReport = () => {
	const theme = useTheme();
	const colors = theme.palette as any;

	const dropdownTheme = useMemo(
		() => ({
			...theme,
			palette: {
				...theme.palette,
				DropdownV2: {
					...colors.DropdownV2,
					background: 'transparent',
					borderColor: '#71758FCC',
					menuItemBackground: '#FFFFFF',
					menuItemText: '#003771',
					menuItemHover: '#EAF5FB',
					menuItemSelectedBg: '#003771',
					menuItemSelectedText: '#FFFFFF',
					borderColorActive: '#003771',
					borderColorHover: '#003771',
					dropdownIconActive: '#003771',
					dropdownIconDefault: '#003771',
				},
			},
		}),
		[theme, colors.DropdownV2],
	);

	const [programOptions, setProgramOptions] =
		useState<SearchableDropdownOption[]>([
			ALL_PROGRAM_OPTION,
		]);

	const [
		environmentOptions,
		setEnvironmentOptions,
	] = useState<SearchableDropdownOption[]>([
		ALL_ENVIRONMENT_OPTION,
	]);

	const [marketOptions, setMarketOptions] =
		useState<SearchableDropdownOption[]>([
			ALL_MARKET_OPTION,
		]);

	const [subProgramOptions, setsubProgramOptions] =
		useState<SearchableDropdownOption[]>([
			ALL_SUBPROGRAM_OPTION,
		]);

	const [selectedProgram, setSelectedProgram] =
		useState<SearchableDropdownOption | null>(
			ALL_PROGRAM_OPTION,
		);

	const [selectedEnvironment, setSelectedEnvironment] =
		useState<SearchableDropdownOption | null>(
			ALL_ENVIRONMENT_OPTION,
		);

	const [selectedMarket, setSelectedMarket] =
		useState<SearchableDropdownOption | null>(
			ALL_MARKET_OPTION,
		);
	const [selectedsubProgram, setSelectedsubProgram] =
		useState<SearchableDropdownOption | null>(
			ALL_SUBPROGRAM_OPTION,
		);

	const [
		serviceWiseTableData,
		setServiceWiseTableData,
	] = useState<MonthlySpendTableRow[]>([]);

	const [serviceWiseMonths, setServiceWiseMonths] =
		useState<MonthDefinition[]>(DEFAULT_MONTHS);

	const [lineChartData, setLineChartData] =
		useState<DynamicLineChartRow[]>([]);

	const [lineChartSeries, setLineChartSeries] =
		useState<LineSeries[]>([]);

	// The line chart API always returns every program's series regardless of
	// the PROGRAM filter (that filter only scopes env/market/division
	// server-side). The chart itself only ever shows one line: the "All
	// Programs" aggregate by default, or the single selected program's line —
	// both resolved client-side here rather than by refetching.
	const visibleLineChartSeries = useMemo(
		() => selectVisibleLineChartSeries(lineChartSeries, selectedProgram),
		[lineChartSeries, selectedProgram],
	);

	const [
		monthWiseTableData,
		setMonthWiseTableData,
	] = useState<MonthlySpendTableRow[]>([]);

	const [monthWiseMonths, setMonthWiseMonths] =
		useState<MonthDefinition[]>(DEFAULT_MONTHS);

	const serviceWiseTableType = 'cloudSpendServiceWise';
	const monthWiseTableType = 'cloudSpendMonthWise';

	const [pieChartColors, setPieChartColors] =
		useState<string[]>(PIE_COLORS);

	const [
		serviceWiseTableLoading,
		setServiceWiseTableLoading,
	] = useState(true);

	const [
		lineChartLoading,
		setLineChartLoading,
	] = useState(true);

	const [
		monthWiseTableLoading,
		setMonthWiseTableLoading,
	] = useState(true);

	const [
		consumptionMetricTableData,
		setConsumptionMetricTableData,
	] = useState<ConsumptionMetricTableRow[]>([]);

	const [
		consumptionMetricTableLoading,
		setConsumptionMetricTableLoading,
	] = useState(true);

	const [
		costAvoidanceTableData,
		setCostAvoidanceTableData,
	] = useState<CostAvoidanceTableRow[]>([]);

	const [
		costAvoidanceTableLoading,
		setCostAvoidanceTableLoading,
	] = useState(true);

	// Raw rows from API — used to recompute tables when visible months change
	const rawProgramCostTrendRowsRef = useRef<ProgramCostTrendTableApiRow[]>([]);
	const rawServiceCostTableRowsRef = useRef<ServiceCostTableApiRow[]>([]);
	const rawCostAvoidanceRowsRef = useRef<Record<string, any>[]>([]);

	const COST_AVOIDANCE_MONTHS = [
		'JAN 2026', 'FEB 2026', 'MAR 2026', 'APR 2026',
		'MAY 2026', 'JUN 2026', 'JUL 2026', 'AUG 2026',
		'SEP 2026', 'OCT 2026', 'NOV 2026', 'DEC 2026',
	];

	// Tracks which months are currently visible in the column picker.
	// Initialised from localStorage so it's correct on first render.
	const getVisibleMonthsFromStorage = () => {
		try {
			const stored = localStorage.getItem('cloudSpendCostAvoidance_user_col_pref');
			if (stored) {
				const names: string[] = JSON.parse(stored);
				const valid = names.filter((name) =>
					COST_AVOIDANCE_MONTHS.includes(name) || name === 'SECTOR' || name === 'TOTAL',
				);
				// If stored preference contains outdated column names from before the API format change, clear it
				if (valid.length <= 2 && names.length > 2) {
					localStorage.removeItem('cloudSpendCostAvoidance_user_col_pref');
					localStorage.removeItem('cloudSpendCostAvoidance_filterPopupOpen');
					return COST_AVOIDANCE_MONTHS;
				}
				const visible = COST_AVOIDANCE_MONTHS.filter((m) =>
					names.includes(m),
				);
				if (visible.length > 0) return visible;
			}
		} catch { /* ignore */ }
		return COST_AVOIDANCE_MONTHS;
	};

	const visibleMonthsRef = useRef<string[]>(getVisibleMonthsFromStorage());
	const [visibleMonths, setVisibleMonths] = useState<string[]>(getVisibleMonthsFromStorage);


	const [
		serviceWiseSorting,
		setServiceWiseSorting,
	] = useState<SortingInfo>({
		sortBy: '',
		sortOrder: '',
	});

	const [
		monthWiseSorting,
		setMonthWiseSorting,
	] = useState<SortingInfo>({
		sortBy: '',
		sortOrder: '',
	});

	const loadInitialDashboardData = useCallback(async () => {
		setServiceWiseTableLoading(true);

		try {
			const programCostTrendTableApiResponse =
				await getProgramCostTrendTable();

			const programCostTrendTableRows:
				ProgramCostTrendTableApiRow[] =
				programCostTrendTableApiResponse?.data?.rows ??
				[];

			rawProgramCostTrendRowsRef.current = programCostTrendTableRows;

			const serviceWiseTable =
				createProgramCostTrendTableData(
					programCostTrendTableRows,
					serviceWiseTableType,
				);

			setServiceWiseTableData(
				serviceWiseTable.data,
			);
			setServiceWiseMonths(
				serviceWiseTable.months,
			);
		} catch (error) {
			console.error(
				'Unable to load program cost trend table',
				error,
			);
			setServiceWiseTableData([]);
			setServiceWiseMonths(DEFAULT_MONTHS);
		} finally {
			setServiceWiseTableLoading(false);
		}

		try {
			const filterOptionsApiResponse =
				await getCloudSpendFilters(null, null);

			const filterOptions: FilterOptionsApiResponse =
				filterOptionsApiResponse?.data ??
				{};

			setProgramOptions([
				ALL_PROGRAM_OPTION,
				...(filterOptions.programs ?? []).map(
					(value) => ({ label: value, value }),
				),
			]);
			setEnvironmentOptions([
				ALL_ENVIRONMENT_OPTION,
				...(filterOptions.envs ?? []).map(
					(value) => ({ label: value, value }),
				),
			]);
		} catch (error) {
			console.error(
				'Unable to load cloud spend filters',
				error,
			);
		}
	}, []);

	const updateMarketFilterOptions = useCallback(async () => {
		try {
			setSelectedMarket(ALL_MARKET_OPTION);
			setSelectedsubProgram(ALL_SUBPROGRAM_OPTION);
			const filterOptionsApiResponse =
				await getCloudSpendFilters(String(selectedProgram?.value), String(selectedMarket?.value));

			const filterOptions: FilterOptionsApiResponse =
				filterOptionsApiResponse?.data ??
				{};


			setMarketOptions([
				ALL_MARKET_OPTION,
				...(filterOptions.markets ?? []).map(
					(value) => ({ label: value, value }),
				),
			]);
			setsubProgramOptions([
				ALL_SUBPROGRAM_OPTION,
				...(filterOptions.divisions ?? []).map(
					(value) => ({ label: value, value }),
				),
			]);
		} catch (error) {
			console.error(
				'Unable to update cloud spend filters',
				error,
			);
		}
	}, [selectedProgram?.value]);

	const updateSubProgramFilterOptions = useCallback(async () => {
		try {
			setSelectedsubProgram(ALL_SUBPROGRAM_OPTION);

			const filterOptionsApiResponse =
				await getCloudSpendFilters(String(selectedProgram?.value), String(selectedMarket?.value));

			const filterOptions: FilterOptionsApiResponse =
				filterOptionsApiResponse?.data ??
				{};

			setsubProgramOptions([
				ALL_SUBPROGRAM_OPTION,
				...(filterOptions.divisions ?? []).map(
					(value) => ({ label: value, value }),
				),
			]);
		} catch (error) {
			console.error(
				'Unable to update cloud spend filters',
				error,
			);
		}
	}, [selectedMarket?.value]);

	const loadProgramCostLineData = useCallback(async () => {
		setLineChartLoading(true);

		const filters = buildRequestBody(
			null,
			selectedEnvironment,
			selectedsubProgram,
			selectedMarket
		);

		try {
			const response = await getProgramCostLine({
				...(filters.env ? { env: filters.env } : {}),
				...(filters.division
					? { division: filters.division }
					: {}),
				...(filters.market ? { market: filters.market } : {}),
			});

			const rows: ProgramCostLineApiRow[] =
				response?.data?.rows ?? [];

			const normalizedLineChart =
				createLineChartData(rows);

			setLineChartData(normalizedLineChart.data);
			setLineChartSeries(normalizedLineChart.series);
		} catch (error) {
			console.error(
				'Unable to load program cost line',
				error,
			);
			setLineChartData([]);
			setLineChartSeries([]);
		} finally {
			setLineChartLoading(false);
		}
	}, [selectedEnvironment, selectedMarket, selectedsubProgram]);

	const loadServiceCostTableData = useCallback(async () => {
		setMonthWiseTableLoading(true);

		const filters = buildRequestBody(
			selectedProgram,
			selectedEnvironment,
			selectedsubProgram,
			selectedMarket
		);

		try {
			const response = await getServiceCostTable(filters);
			const rows: ServiceCostTableApiRow[] =
				response?.data?.rows ?? [];
			rawServiceCostTableRowsRef.current = rows;
			const table = createServiceCostTableData(rows, monthWiseTableType);

			setMonthWiseTableData(table.data);
			setMonthWiseMonths(table.months);
		} catch (error) {
			console.error(
				'Unable to load service cost table',
				error,
			);
			setMonthWiseTableData([]);
			setMonthWiseMonths(DEFAULT_MONTHS);
		} finally {
			setMonthWiseTableLoading(false);
		}
	}, [
		selectedProgram,
		selectedEnvironment,
		selectedsubProgram,
		selectedMarket
	]);

	// Top-5-services pie is derived from the already-loaded service cost
	// table (sorted by Grand Total) instead of a dedicated API call.
	const pieChartData = useMemo(
		() => createServiceTopFivePieData(monthWiseTableData),
		[monthWiseTableData],
	);

	useEffect(() => {
		setPieChartColors(PIE_COLORS);
	}, []);

	useEffect(() => {
		loadInitialDashboardData();
	}, [loadInitialDashboardData]);

	useEffect(() => {
		loadProgramCostLineData();
	}, [loadProgramCostLineData]);

	useEffect(() => {
		loadServiceCostTableData();
	}, [loadServiceCostTableData]);

	const loadConsumptionMetricTableData = useCallback(async () => {
		setConsumptionMetricTableLoading(true);
		try {
			const response = await getConsumptionMetricTable();
			const rows: Record<string, any>[] = response?.data?.rows ?? [];
			const tableRows: ConsumptionMetricTableRow[] = rows.map(
				(row, index) => ({ id: index + 1, CONSUMPTION_METRIC: String(row.CONSUMPTION_METRIC ?? ''), ...row }),
			);
			setConsumptionMetricTableData(tableRows);
		} catch (error) {
			console.error('Unable to load consumption metric table', error);
			setConsumptionMetricTableData([]);
		} finally {
			setConsumptionMetricTableLoading(false);
		}
	}, []);

	useEffect(() => {
		loadConsumptionMetricTableData();
	}, [loadConsumptionMetricTableData]);

	const buildCostAvoidanceTable = useCallback(
		(rawRows: Record<string, any>[], months: string[]): CostAvoidanceTableRow[] => {
			const dataRows: CostAvoidanceTableRow[] = rawRows.map((row, index) => {
				const built: CostAvoidanceTableRow = {
					id: index + 1,
					Sector: String(row.Sector ?? '').trim(),
				};
				COST_AVOIDANCE_MONTHS.forEach((m) => {
					built[m] = Number(row[m] ?? 0);
				});
				built.total = months.reduce((sum, m) => sum + Number(row[m] ?? 0), 0);
				return built;
			});

			// Total row
			const totalRow: CostAvoidanceTableRow = {
				id: rawRows.length + 1,
				Sector: 'Grand Total',
			};
			COST_AVOIDANCE_MONTHS.forEach((m) => {
				totalRow[m] = rawRows.reduce((sum, row) => sum + Number(row[m] ?? 0), 0);
			});
			totalRow.total = months.reduce(
				(sum, m) => sum + rawRows.reduce((s, row) => s + Number(row[m] ?? 0), 0),
				0,
			);

			return [...dataRows, totalRow];
		},
		[],
	);

	const loadCostAvoidanceTableData = useCallback(async () => {
		setCostAvoidanceTableLoading(true);
		try {
			const response = await getCostAvoidanceTable();
			const rows: Record<string, any>[] = response?.data?.rows ?? [];
			rawCostAvoidanceRowsRef.current = rows;
			const months = visibleMonthsRef.current;
			setCostAvoidanceTableData(buildCostAvoidanceTable(rows, months));
		} catch (error) {
			console.error('Unable to load cost avoidance table', error);
			setCostAvoidanceTableData([]);
		} finally {
			setCostAvoidanceTableLoading(false);
		}
	}, [buildCostAvoidanceTable]);

	useEffect(() => {
		loadCostAvoidanceTableData();
	}, [loadCostAvoidanceTableData]);

	// Listen for column picker saves and recompute totals reactively
	useEffect(() => {
		const handleStorage = (e: StorageEvent) => {
			if (e.key === 'cloudSpendServiceWise_user_col_pref') {
				if (rawProgramCostTrendRowsRef.current.length > 0) {
					const serviceWiseTable = createProgramCostTrendTableData(
						rawProgramCostTrendRowsRef.current,
						serviceWiseTableType,
					);
					setServiceWiseTableData(serviceWiseTable.data);
				}
			}
			if (e.key === 'cloudSpendMonthWise_user_col_pref') {
				if (rawServiceCostTableRowsRef.current.length > 0) {
					const monthWiseTable = createServiceCostTableData(
						rawServiceCostTableRowsRef.current,
						monthWiseTableType,
					);
					setMonthWiseTableData(monthWiseTable.data);
				}
			}
			if (e.key === 'cloudSpendCostAvoidance_user_col_pref') {
				const newMonths = getVisibleMonthsFromStorage();
				visibleMonthsRef.current = newMonths;
				setVisibleMonths(newMonths);
				const rows = rawCostAvoidanceRowsRef.current;
				if (rows.length > 0) {
					setCostAvoidanceTableData(buildCostAvoidanceTable(rows, newMonths));
				}
			}
		};
		window.addEventListener('storage', handleStorage);
		return () => window.removeEventListener('storage', handleStorage);
	}, [serviceWiseTableType, monthWiseTableType, buildCostAvoidanceTable]);

	useEffect(() => {
		updateMarketFilterOptions();
	}, [updateMarketFilterOptions]);

	useEffect(() => {
		updateSubProgramFilterOptions();
	}, [updateSubProgramFilterOptions]);

	const handleServiceWiseSorting = (
		sorting: SortingInfo,
	) => {
		setServiceWiseSorting(sorting);
	};

	const handleMonthWiseSorting = (
		sorting: SortingInfo,
	) => {
		setMonthWiseSorting(sorting);
	};

	const handleProgramChange = (
		program: SearchableDropdownOption | null,
	) => {
		console.log(
			'Cloud spend program changed:',
			program?.value,
		);
	};

	const handleMarketChange = (
		market: SearchableDropdownOption | null,
	) => {
		console.log(
			'Cloud spend market changed:',
			market?.value,
		);
	};

	const handleEnvironmentChange = (
		environment: SearchableDropdownOption | null,
	) => {
		console.log(
			'Cloud spend environment changed:',
			environment?.value,
		);
	};

	const handleDivisionChange = (
		division: SearchableDropdownOption | null,
	) => {
		console.log(
			'Cloud spend division changed:',
			division?.value,
		);
	};

	const serviceWiseTableColumns = useMemo(
		() =>
			getColumns(
				serviceWiseMonths,
				formatMillionsValue,
				'PROGRAM',
			),
		[serviceWiseMonths],
	);

	const monthWiseTableColumns = useMemo(
		() =>
			getColumns(
				monthWiseMonths,
				formatValue,
			),
		[monthWiseMonths],
	);

	// Derived directly from the actual table columns (not a hardcoded month
	// list) so every column present in the data — including new forecast
	// months as they arrive — is selected by default without needing a
	// manual "select all" from the user.
	// const serviceWiseDefaultColumnNames = useMemo(
	// 	() => serviceWiseTableColumns.map((col) => col.name as string),
	// 	[serviceWiseTableColumns],
	// );

	const serviceWiseDefaultColumnNames = useMemo(
		() => serviceWiseTableColumns.map((col) => col.name as string),
		[serviceWiseTableColumns],
	);

	const monthWiseDefaultColumnNames = useMemo(
		() => monthWiseTableColumns.map((col) => col.name as string),
		[monthWiseTableColumns],
	);

	const CONSUMPTION_METRIC_COLUMNS: Array<{
		key: string;
		name: string;
		resizable: boolean;
		minWidth: number;
		frozen?: boolean;
		formatter: (props: { row: ConsumptionMetricTableRow }) => JSX.Element;
	}> = [
			{
				key: 'CONSUMPTION_METRIC',
				name: 'CONSUMPTION METRICS',
				resizable: true,
				minWidth: 200,
				frozen: true,
				formatter: ({ row }: { row: ConsumptionMetricTableRow }) =>
					renderTableValue(row.CONSUMPTION_METRIC),
			},
			{
				key: 'FY-2025',
				name: 'FY 2025 (Actuals)',
				resizable: true,
				minWidth: 165,
				formatter: ({ row }: { row: ConsumptionMetricTableRow }) =>
					renderTableValue(String(row['FY-2025'] ?? '')),
			},
			{
				key: 'FY-2026',
				name: 'FY 2026 (6+6)',
				resizable: true,
				minWidth: 155,
				formatter: ({ row }: { row: ConsumptionMetricTableRow }) =>
					renderTableValue(String(row['FY-2026'] ?? '')),
			},
			{
				key: '2027',
				name: '2027 New Increment',
				resizable: true,
				minWidth: 175,
				formatter: ({ row }: { row: ConsumptionMetricTableRow }) =>
					renderTableValue(String(row['2027'] ?? '')),
			},
			{
				key: '2027-Requested-AOP',
				name: '2027 Requested AOP',
				resizable: true,
				minWidth: 180,
				formatter: ({ row }: { row: ConsumptionMetricTableRow }) =>
					renderTableValue(String(row['2027-Requested-AOP'] ?? '')),
			},
		];

	const consumptionMetricColumns = useMemo(
		() => CONSUMPTION_METRIC_COLUMNS,
		[],
	);

	const consumptionMetricDefaultColumnNames = useMemo(
		() => consumptionMetricColumns.map((col) => col.name as string),
		[consumptionMetricColumns],
	);

	const costAvoidanceColumns = useMemo(
		() => [
			{
				key: 'Sector',
				name: 'SECTOR',
				resizable: true,
				minWidth: 220,
				frozen: true,
				formatter: ({ row }: { row: CostAvoidanceTableRow }) =>
					renderTableValue(row.Sector, row.Sector === 'Grand Total' ? 700 : 500),
			},
			...COST_AVOIDANCE_MONTHS.map((month) => ({
				key: month,
				name: month.toUpperCase(),
				resizable: true,
				minWidth: 120,
				formatter: ({ row }: { row: CostAvoidanceTableRow }) =>
					renderTableValue(
						row[month] != null
							? Number(row[month]).toLocaleString('en-US')
							: '',
						row.Sector === 'Grand Total' ? 700 : 500,
					),
			})),
			{
				key: 'total',
				name: 'GRAND TOTAL',
				resizable: true,
				minWidth: 130,
				formatter: ({ row }: { row: CostAvoidanceTableRow }) =>
					renderTableValue(
						Number(row.total ?? 0).toLocaleString('en-US'),
						700,
					),
			},
		],
		[visibleMonths],
	);

	const costAvoidanceDefaultColumnNames = useMemo(
		() => costAvoidanceColumns.map((col) => col.name as string),
		[costAvoidanceColumns],
	);

	return (
		<PageWrapper>
			<GlobalStyles
				styles={{
					'.rdg-row:nth-of-type(odd) .forecast-month-cell': {
						backgroundColor: `${FORECAST_ODD_CELL_BG} !important`,
					},
					'.rdg-row:nth-of-type(even) .forecast-month-cell': {
						backgroundColor: `${FORECAST_EVEN_CELL_BG} !important`,
					},
					'.forecast-month-header': {
						backgroundColor: `${FORECAST_HEADER_BG} !important`,
					},
				}}
			/>

			<Typography
				variant="widgetTitleNew"
				mb={2}
				sx={{
					color: colors.widget.text.title,
					fontWeight: 600,
				}}
			>
				D&AI Spend Overview
			</Typography>

			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Box
						p={2}
						borderRadius="4px"
						sx={{ backgroundColor: `${colors.widget.background.card} !important` }}
					>
						<CustomizedDataGrid
							title="Program Wise Cost in $ Millions"
							tableColumns={serviceWiseTableColumns}
							tableData={serviceWiseTableData}
							tableType={serviceWiseTableType}
							defaultColumnNames={serviceWiseDefaultColumnNames}
							listLength={serviceWiseTableData.length}
							isFetching={serviceWiseTableLoading}
							isRenderPagination={false}
							isShowMoreButton={false}
							autoSetDraggedColumns
							isLighterTheme
							onSorting={handleServiceWiseSorting}
							currSorting={serviceWiseSorting}
							isDateRangeFilter={false}
							isDisabled={false}
							showExport={false}
						/>
					</Box>
				</Grid>
			</Grid>

			<Divider
				sx={{
					my: 3,
					borderColor:
						colors.widget?.border?.main ??
						'#AABBD4',
				}}
			/>

			<Typography
				variant="widgetTitleNew"
				mb={2}
				sx={{
					color: colors.widget.text.title,
					fontWeight: 600,
				}}
			>
				Program-wise Azure Consumed Service Costs
			</Typography>

			<Grid container spacing={2} alignItems="flex-start">
				<Grid item xs={12} lg={6}>
					<Box sx={{ width: '100%' }}>
						<ThemeProvider theme={dropdownTheme as any}>
							<Stack
								direction={{ xs: 'column', sm: 'row' }}
								flexWrap="wrap"
								gap={2}
								alignItems={{ xs: 'stretch', sm: 'center' }}
								justifyContent="flex-start"
								mb={2}
							>
								<Box sx={{ minWidth: 160, flex: '1 1 160px' }}>
									<SearchableDropdown
										value={selectedProgram}
										setValue={setSelectedProgram}
										options={programOptions}
										handleChange={handleProgramChange}
										placeholder="PROGRAM"
										boxProps={{
											sx: {
												width: '100%',
												'& .MuiOutlinedInput-root': {
													height: '48px',
													'& fieldset': {
														borderColor: `${colors.DropdownV2?.borderColorActive} !important`,
													},
													'&:hover fieldset': {
														borderColor: `${colors.DropdownV2?.borderColorActive} !important`,
													},
													'&.Mui-focused fieldset': {
														borderColor: `${colors.DropdownV2?.borderColorActive} !important`,
													},
												},
												'& .MuiInputBase-input': {
													fontWeight: 600,
													textTransform: 'uppercase',
												},
											},
										}}
									/>
								</Box>

								<Box sx={{ minWidth: 160, flex: '1 1 160px' }}>
									<SearchableDropdown
										value={selectedEnvironment}
										setValue={setSelectedEnvironment}
										options={environmentOptions}
										handleChange={handleEnvironmentChange}
										placeholder="ENVIRONMENT"
										boxProps={{
											sx: {
												width: '100%',
												'& .MuiOutlinedInput-root': {
													height: '48px',
													'& fieldset': {
														borderColor: `${colors.DropdownV2?.borderColorActive} !important`,
													},
													'&:hover fieldset': {
														borderColor: `${colors.DropdownV2?.borderColorActive} !important`,
													},
													'&.Mui-focused fieldset': {
														borderColor: `${colors.DropdownV2?.borderColorActive} !important`,
													},
												},
												'& .MuiInputBase-input': {
													fontWeight: 600,
													textTransform: 'uppercase',
												},
											},
										}}
									/>
								</Box>

								<Box sx={{ minWidth: 160, flex: '1 1 160px' }}>
									<SearchableDropdown
										value={selectedMarket}
										setValue={setSelectedMarket}
										options={marketOptions}
										handleChange={handleMarketChange}
										disabled={selectedProgram?.value === 'all'}
										placeholder="MARKET"
										boxProps={{
											sx: {
												width: '100%',
												'& .MuiOutlinedInput-root': {
													height: '48px',
													'& fieldset': {
														borderColor: `${colors.DropdownV2?.borderColorActive} !important`,
													},
													'&:hover fieldset': {
														borderColor: `${colors.DropdownV2?.borderColorActive} !important`,
													},
													'&.Mui-focused fieldset': {
														borderColor: `${colors.DropdownV2?.borderColorActive} !important`,
													},
												},
												'& .MuiInputBase-input': {
													fontWeight: 600,
													textTransform: 'uppercase',
												},
											},
										}}
									/>
								</Box>

								<Box sx={{ minWidth: 160, flex: '1 1 160px' }}>
									<SearchableDropdown
										value={selectedsubProgram}
										setValue={setSelectedsubProgram}
										options={subProgramOptions}
										handleChange={handleDivisionChange}
										disabled={selectedProgram?.value === 'all'}
										placeholder="SUB PROGRAM"
										boxProps={{
											sx: {
												width: '100%',
												'& .MuiOutlinedInput-root': {
													height: '48px',
													'& fieldset': {
														borderColor: `${colors.DropdownV2?.borderColorActive} !important`,
													},
													'&:hover fieldset': {
														borderColor: `${colors.DropdownV2?.borderColorActive} !important`,
													},
													'&.Mui-focused fieldset': {
														borderColor: `${colors.DropdownV2?.borderColorActive} !important`,
													},
												},
												'& .MuiInputBase-input': {
													fontWeight: 600,
													textTransform: 'uppercase',
												},
											},
										}}
									/>
								</Box>
							</Stack>
						</ThemeProvider>

						<MultiLineChartWidget
							title="Cost by Program (MoM) in $ Millions"
							isLoading={lineChartLoading}
							data={lineChartData}
							xKey="month"
							xAxisLabel="Month"
							series={visibleLineChartSeries}
							showGridLines
							hasTooltip={false}
							legendPosition="bottom"
							forecastStartMonth="JUL 2026"
							yMin={0}
							showSeriesTotal
							showEndTotal
							showValuesInMillions
							yInterval={0.5}
							yAxisFormatter={(value: number) =>
								Number.isFinite(value)
									? `${Number.parseFloat(value.toFixed(2))}M`
									: ''
							}
							height={620}
							tooltipFormatter={(params: any[]) => {
								if (!params?.length) {
									return '';
								}

								const rows = params
									.map(
										(item) =>
											`${item.marker} ${item.seriesName
											}: <b>${Number(
												item.value,
											).toLocaleString(
												'en-US',
												{
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												},
											)}M</b>`,
									)
									.join('<br/>');

								return `<b>${params[0].axisValue}</b><br/>${rows}`;
							}}
						/>
					</Box>
				</Grid>

				<Grid item xs={12} lg={6}>
					<Stack gap={2} sx={{ width: '100%' }}>
						<Box
							p={2}
							borderRadius="4px"
							sx={{
								backgroundColor: `${colors.widget.background.card} !important`,
								'& .rdg': {
									maxHeight: '316px !important',
								},
							}}
						>
							<CustomizedDataGrid
								title="Month-wise cost by Azure Service in $ Millions"
								tableColumns={monthWiseTableColumns}
								tableData={monthWiseTableData}
								tableType={monthWiseTableType}
								defaultColumnNames={monthWiseDefaultColumnNames}
								listLength={monthWiseTableData.length}
								isFetching={monthWiseTableLoading}
								isRenderPagination={false}
								isShowMoreButton={false}
								autoSetDraggedColumns
								isLighterTheme
								rowHeight={38}
								headerRowHeight={42}
								onSorting={handleMonthWiseSorting}
								currSorting={monthWiseSorting}
								isDateRangeFilter={false}
								isDisabled={false}
								showExport={false}
							/>
						</Box>

						<Box>
							<ThreeDPieChart
								title="Total Cost by Top Azure Service in $ Millions"
								isLoading={monthWiseTableLoading}
								seriesData={pieChartData}
								pieColors={pieChartColors}
								showDataLabels
								showLegend
								showTooltip
								pieRadius="58%"
								center={['50%', '41%']}
								depth={18}
								valueType="cost"
								decimalPlaces={2}
								legendPosition="left"
								labelLineLength={16}
								chartHeight={300}
							/>
						</Box>
					</Stack>
				</Grid>
			</Grid>

			<Divider
				sx={{
					my: 3,
					borderColor:
						colors.widget?.border?.main ??
						'#AABBD4',
				}}
			/>

			<Typography
				variant="widgetTitleNew"
				mb={2}
				sx={{
					color: colors.widget.text.title,
					fontWeight: 600,
				}}
			>
				Consumption Metrics
			</Typography>

			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Box
						p={2}
						borderRadius="4px"
						sx={{ backgroundColor: `${colors.widget.background.card} !important` }}
					>
						<CustomizedDataGrid
							tableColumns={consumptionMetricColumns}
							tableData={consumptionMetricTableData}
							tableType="cloudSpendConsumptionMetric"
							defaultColumnNames={consumptionMetricDefaultColumnNames}
							listLength={consumptionMetricTableData.length}
							isFetching={consumptionMetricTableLoading}
							isRenderPagination={false}
							isShowMoreButton={false}
							autoSetDraggedColumns
							isLighterTheme
							rowHeight={38}
							headerRowHeight={42}
							isDateRangeFilter={false}
							isDisabled={false}
							showExport={false}
						/>
					</Box>
				</Grid>
			</Grid>

			<Divider
				sx={{
					my: 3,
					borderColor:
						colors.widget?.border?.main ??
						'#AABBD4',
				}}
			/>

			<Typography
				variant="widgetTitleNew"
				mb={2}
				sx={{
					color: colors.widget.text.title,
					fontWeight: 600,
				}}
			>
				Cost Avoidance
			</Typography>

			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Box
						p={2}
						borderRadius="4px"
						sx={{ backgroundColor: `${colors.widget.background.card} !important` }}
					>
						<CustomizedDataGrid
							tableColumns={costAvoidanceColumns}
							tableData={costAvoidanceTableData}
							tableType="cloudSpendCostAvoidance"
							defaultColumnNames={costAvoidanceDefaultColumnNames}
							listLength={costAvoidanceTableData.length}
							isFetching={costAvoidanceTableLoading}
							isRenderPagination={false}
							isShowMoreButton={false}
							autoSetDraggedColumns
							isLighterTheme
							rowHeight={38}
							headerRowHeight={42}
							isDateRangeFilter={false}
							isDisabled={false}
							showExport={false}
						/>
					</Box>
				</Grid>
			</Grid>
		</PageWrapper>
	);
};

export default CloudSpendFinOpsReport;